<template>
    <LoadingComponent :props="loading" />
    <section class="mb-12 sm:mb-12 mt-12 sm:mt-12">
        <div class="container">
            <div class="row">
                <div class="col-12">
                    <CategoryBreadcrumbComponent :categories="categories" :productName="displayProduct.name" />
                </div>

                <div v-if="images.length" class="col-12 sm:col-6 lg:col-5">
                    <Swiper dir="ltr" :spaceBetween="10" :navigation="true" :thumbs="{ swiper: thumbsSwiper }"
                        :modules="modules" class="gallery-swiper rounded-lg product-img-swiper">
                        <SwiperSlide v-for="(image, index) in images" :key="index" class="w-full">
                            <inner-image-zoom :src="image" :zoomSrc="image" :zoomScale='1' zoomType="hover"
                                :hideHint='true' />
                        </SwiperSlide>
                    </Swiper>

                    <Swiper dir="ltr" @swiper="setThumbsSwiper" :spaceBetween="12" :slidesPerView="4" :freeMode="true"
                        :watchSlidesProgress="true" :modules="modules" class="thumb-swiper">
                        <SwiperSlide v-for="(image, index) in images" :key="index"
                            class="w-full cursor-pointer rounded-lg border border-gray-200 transition-all duration-500">
                            <img class="w-full rounded-lg transition-all duration-500"
                                :src="image" alt="gallery" />
                        </SwiperSlide>
                    </Swiper>
                </div>

                <div v-else class="col-12 sm:col-6 lg:col-5">
                    <img v-if="displayProduct.image" :src="displayProduct.image" alt="products" class="w-full rounded-lg">
                </div>

                <div class="col-12 sm:col-6 lg:col-7 lg:pl-10">
                    <h2 class="text-2xl sm:text-3xl font-semibold capitalize mt-3 mb-2">{{ displayProduct.name }}</h2>

                    <div class="flex flex-wrap items-center gap-3 border-b mb-5 pb-5">
                        <h3 class="flex items-start gap-4">
                            <span class="text-2xl font-bold">
                                {{
                                    currencyFormat(temp.price, setting.site_digit_after_decimal_point,
                                        setting.site_default_currency_symbol, setting.site_currency_position)
                                }}
                            </span>
                            <del v-if="displayProduct.is_offer" class="text-lg font-bold text-red">
                                {{
                                    currencyFormat(temp.oldPrice, setting.site_digit_after_decimal_point,
                                        setting.site_default_currency_symbol, setting.site_currency_position)
                                }}
                            </del>
                        </h3>
                        <div v-if="setting && setting.site_reviews_show_publicly === enums.activityEnum.ENABLE" class="flex flex-wrap items-center border-l pl-4 gap-2">
                            <starRating border-color="#FFBC1F" :rounded-corners="true" :padding="2.5" :border-width="2.5"
                                :star-size="11" class="-mt-0.5" inactive-color="#FFFFFF" active-color="#FFBC1F"
                                :round-start-rating="false" :show-rating="false" :read-only="true" :max-rating="5"
                                :rating="(displayProduct.rating_star / displayProduct.rating_star_count)" />
                                <div v-if="displayProduct.rating_star_count > 0" class="flex items-center gap-1">
                                    <span class="text-base font-medium whitespace-nowrap text-text">
                                        {{ (displayProduct.rating_star / displayProduct.rating_star_count).toFixed(1) }}
                                    </span>
                                    <span
                                        class="text-base font-medium whitespace-nowrap text-text hover:text-primary cursor-pointer">
                                        ({{
                                            displayProduct.rating_star_count
                                        }} {{ displayProduct.rating_star_count > 1 ? $t('label.reviews') : $t('label.review') }})
                                    </span>
                                </div>
                        </div>
                    </div>

                    <!-- Short description and features -->
                    <div id="features" class="flex flex-wrap items-center">
                        <div v-if="displayProduct.brand || displayProduct.short_description || (displayProduct.custom_fields && displayProduct.custom_fields.length)" class="w-full">
                            <div v-if="displayProduct.brand || displayProduct.short_description || (displayProduct.custom_fields && displayProduct.custom_fields.length)">
                                <ul class="grid grid-cols-1 gap-1">
                                    <li v-if="displayProduct.short_description" class="flex items-start gap-2">
                                        <span class="font-normal text-base"><strong>{{ $t('label.description') }}</strong>: {{ displayProduct.short_description }}</span>
                                    </li>
                                    <li v-if="displayProduct.brand" class="flex items-start gap-2">
                                        <span class="font-normal text-base"><strong>{{ $t('label.brand') }}</strong>: {{ displayProduct.brand }}</span>
                                    </li>
                                    <li v-for="(field, idx) in displayProduct.custom_fields" :key="idx"
                                        class="flex items-start gap-2">
                                        <span class="font-bold text-base">{{ field.label }}:</span>
                                        <span class="text-normal text-base">{{ field.value }}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-wrap items-center gap-2 border-b mb-5 pb-5 w-full"></div>

                    <VariationComponent v-if="initialVariations.length > 0 && variationComponent"
                        :method="selectedVariationMethod" :variations="initialVariations" />

                    <dl class="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8">
                        <dt class="capitalize text-lg font-semibold">{{ $t('label.quantity') }}:</dt>
                        <dd class="flex items-center gap-6">
                            <div class="flex items-center gap-1 w-20 p-1 rounded-full bg-[#F7F7FC]">
                                <button @click.prevent="quantityDecrement" type="button"
                                    :class="temp.quantity === 1 ? 'cursor-not-allowed' : ''"
                                    class="lab-fill-circle-minus text-lg leading-none transition-all duration-300 hover:text-primary"></button>
                                <input type="number" v-model="temp.quantity" v-on:keypress="onlyNumber($event)"
                                    v-on:keyup="quantityUp" class="text-center w-full h-5 text-sm font-medium">
                                <button @click.prevent="quantityIncrement" type="button"
                                    :class="temp.stock === temp.quantity ? 'cursor-not-allowed' : temp.quantity === temp.maximum_purchase_quantity ? 'cursor-not-allowed' : ''"
                                    class="lab-fill-circle-plus text-lg leading-none transition-all duration-300 hover:text-primary"></button>
                            </div>
                            <div v-if="(!initialVariations.length || selectedVariation != null)">
                                <p v-if="temp.stock > 0" class="font-medium text-gray-500">
                                    <span v-if="temp.purchasable">
                                        <span class="text-success">{{ $t('label.available') }}</span>
                                        <span v-if="temp.stock_visibility === 'visible'"> (<b>{{ temp.stock }}</b> <span class="lowercase">{{ temp.stock == 1 ? $t('label.unit') : $t('label.units') }}</span>)</span>
                                        <span v-if="temp.stock_visibility !== 'visible' && temp.stock == 1">&nbsp;(<span class="lowercase">{{ $t('label.close_low_stock') }}</span>)</span>
                                    </span>
                                    <span v-else class="text-success">
                                        {{ $t('label.available') }}
                                    </span>
                                </p>
                                <p v-else class="text-danger">
                                    {{ $t('label.stock_out') }}
                                </p>
                            </div>
                        </dd>
                    </dl>

                    <dl v-if="temp.quantity > 1" class="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8">
                        <dt class="capitalize text-lg font-semibold">{{ $t('label.total_price') }}:</dt>
                        <dd class="flex items-center gap-6 text-green-500 font-semibold text-lg">
                            {{
                                currencyFormat(temp.totalPrice, setting.site_digit_after_decimal_point,
                                    setting.site_default_currency_symbol, setting.site_currency_position)
                            }}
                        </dd>
                    </dl>

                    <div class="flex flex-wrap items-center gap-3 mb-10">
                        <button @click.prevent="addToCart" :disabled="enableAddToCardButton || isAddingToCart" type="button"
                            :class="[
                                enableAddToCardButton === false ? 'shadow-btn-primary !bg-primary' : 'bg-slate-400',
                                isAddingToCart ? 'pointer-events-none opacity-80' : ''
                            ]"
                            class="flex items-center gap-3 px-8 h-12 leading-12 rounded-full transition-all duration-500 text-white">
                            <template v-if="isAddingToCart">
                                <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span class="whitespace-nowrap font-bold">{{ $t('button.adding') }}</span>
                            </template>
                            <template v-else>
                                <i class="lab-line-bag text-xl"></i>
                                <span class="whitespace-nowrap font-bold">{{ $t("button.add_to_cart") }}</span>
                            </template>
                        </button>
                        <button type="button" @click="wishlist(!displayProduct.wishlist)" :disabled="isTogglingWishlist"
                            :class="isTogglingWishlist ? 'pointer-events-none opacity-80' : ''"
                            class="text-gray-500 flex items-center gap-2 px-8 h-9 leading-12 border rounded-full transition-all duration-500 bg-white">
                            <template v-if="isTogglingWishlist">
                                <svg class="inline-block animate-spin text-danger" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                    <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                </svg>
                                <span class="whitespace-nowrap font-medium">{{ $t('button.processing') }}</span>
                            </template>
                            <template v-else>
                                <i :class="displayProduct.wishlist ? 'fa-regular fa-heart' : 'fa-solid fa-heart'"
                                    class="text-xl text-danger"></i>
                                <span class="whitespace-nowrap font-medium">{{ $t('button.favorite') }}</span>
                            </template>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section :class="relatedProducts.length > 0 ? 'mb-0' : 'mb-18 sm:mb-20'">
        <div class="container">
            <div class="row">
                <div class="col-12">
                    <div class="rounded-lg border border-[#D9DBE9]">
                        <nav class="flex flex-wrap items-center p-4 sm:py-4 sm:px-4 gap-2 sm:gap-3">
                            <button type="button"
                                @click.prevent="multiTargets($event, 'tab-btn', 'tab-div', 'tab_details')"
                                class="tab-btn active text-sm sm:text-base font-medium leading-5 capitalize py-2 sm:py-2.5 px-5 sm:px-8 rounded-full border border-[#D9DBE9]">
                                {{ $t('label.details') }}
                            </button>

                            <button type="button" v-if="videos.length > 0"
                                @click.prevent="multiTargets($event, 'tab-btn', 'tab-div', 'tab_videos')"
                                class="tab-btn text-sm sm:text-base font-medium leading-5 capitalize py-2 sm:py-2.5 px-5 sm:px-8 rounded-full border border-[#D9DBE9]">
                                {{ $t('label.videos') }}
                            </button>

                            <button type="button" v-if="setting && setting.site_reviews_show_publicly === enums.activityEnum.ENABLE && reviews.length > 0"
                                @click.prevent="multiTargets($event, 'tab-btn', 'tab-div', 'tab_reviews')"
                                class="tab-btn text-sm sm:text-base font-medium leading-5 capitalize py-2 sm:py-2.5 px-5 sm:px-8 rounded-full border border-[#D9DBE9]">
                                {{ $t('label.reviews') }}
                            </button>
                            <button type="button" v-if="displayProduct.shipping_and_return || (setting && setting.shipping_setup_product_shipping_and_return_default_text)"
                                @click.prevent="multiTargets($event, 'tab-btn', 'tab-div', 'tab_shipping_and_return')"
                                class="tab-btn text-sm sm:text-base font-medium leading-5 capitalize py-2 sm:py-2.5 px-5 sm:px-8 rounded-full border border-[#D9DBE9]">
                                {{ $t('label.shipping_and_return') }}
                            </button>
                        </nav>

                        <div id="tab_details" class="tab-div active p-4 sm:p-4 sm:py-6 border-t border-[#D9DBE9] min-h-[200px]">
                            <h3 class="capitalize text-2xl sm:text-3xl font-semibold mb-2">
                                {{ $t('label.product_details') }}
                            </h3>
                            <div class="text-description" v-html="displayProduct.details"></div>
                        </div>

                        <div id="tab_videos" class="tab-div p-4 sm:p-4 sm:py-6 border-t border-[#D9DBE9] min-h-[200px]">
                            <h3 class="capitalize text-2xl sm:text-3xl font-semibold mb-2">
                                {{ $t('label.product_videos') }}
                            </h3>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <iframe v-for="video in videos" :src="video.link"
                                    class="w-full h-40 sm:h-64 rounded-2xl"></iframe>
                            </div>
                        </div>

                        <div id="tab_reviews" v-if="setting && setting.site_reviews_show_publicly === enums.activityEnum.ENABLE" class="tab-div p-4 sm:p-4 sm:py-6 border-t border-[#D9DBE9] min-h-[200px]">
                            <h3 class="capitalize text-2xl sm:text-3xl font-semibold mb-2">
                                {{ $t('label.product_reviews') }}
                            </h3>
                            <div class="flex flex-wrap items-center gap-2 border-b  mb-6 pb-6">
                                <starRating border-color="#FFBC1F" :rounded-corners="true" :padding="2.5"
                                    :border-width="2.5" :star-size="11" class="-mt-0.5" inactive-color="#FFFFFF"
                                    active-color="#FFBC1F" :round-start-rating="false" :show-rating="false"
                                    :read-only="true" :max-rating="5"
                                    :rating="(displayProduct.rating_star / displayProduct.rating_star_count)" />
                                <div v-if="displayProduct.rating_star_count > 0" class="flex items-center gap-1">
                                    <span class="text-base font-medium whitespace-nowrap text-text">
                                        {{ (displayProduct.rating_star / displayProduct.rating_star_count).toFixed(1) }}
                                    </span>
                                    <span
                                        class="text-base font-medium whitespace-nowrap text-text hover:text-primary cursor-pointer">
                                        ({{
                                            displayProduct.rating_star_count
                                        }} {{
                                            displayProduct.rating_star_count > 1 ? $t('label.reviews') : $t('label.review')
                                        }})
                                    </span>
                                </div>
                            </div>

                            <div v-for="review in reviews" class="mb-8">
                                <h4 class="text-lg font-semibold capitalize mb-2">{{ review.name }}</h4>
                                <div class="flex flex-wrap items-center gap-2 mb-3">
                                    <starRating border-color="#FFBC1F" inactive-color="#FFFFFF" active-color="#FFBC1F"
                                        :rounded-corners="true" :padding="2.5" :border-width="2.5" :star-size="11"
                                        class="-mt-0.5" :round-start-rating="false" :show-rating="false"
                                        :read-only="true" :max-rating="5" :rating="review.star" />
                                </div>
                                <p class="mb-4">{{ review.review }}</p>


                                <div class="flex flex-wrap gap-4" v-if="review.images.length > 0">
                                    <img v-for="reviewImage in review.images" :src="reviewImage" alt="image"
                                        class="w-20 rounded-lg">
                                </div>
                            </div>

                            <button v-if="displayProduct.rating_star_count > reviews.length" @click.prevent="readMore"
                                type="button" class="flex items-center justify-center gap-3 w-fit mx-auto text-primary">
                                <span class="text-lg font-medium capitalize">{{ $t('label.read_more') }}</span>
                                <i class="lab-line-down-arrow text-sm -mt-1"></i>
                            </button>
                        </div>

                        <div id="tab_shipping_and_return" class="tab-div p-4 sm:p-4 sm:py-6 border-t border-[#D9DBE9] min-h-[200px]">
                            <h3 class="capitalize text-2xl sm:text-3xl font-semibold mb-2">
                                {{ $t('label.product_shipping_and_return') }}</h3>
                            <div class="text-description" v-html="displayProduct.shipping_and_return ? displayProduct.shipping_and_return : (setting ? setting.shipping_setup_product_shipping_and_return_default_text : '')"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section v-if="relatedProducts.length > 0" class="mb-18 sm:mb-20 mt-12 sm:mt-12">
        <div class="container">
            <div class="flex items-center justify-between gap-4 mb-5 sm:mb-7">
                <h2 class="text-1xl sm:text-2xl font-semibold  capitalize">
                    {{ $t('label.related_products') }}
                </h2>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                <ProductListComponent v-if="relatedProducts.length > 0" :products="relatedProducts" />
            </div>
        </div>
    </section>

</template>

<script>
import { ref } from "vue";
import axios from 'axios';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { FreeMode, Navigation, Thumbs } from 'swiper/modules';
import LoadingComponent from "../components/LoadingComponent";
import starRating from "vue-star-rating";
import targetService from "../../../services/targetService";
import router from "../../../router";
import CategoryBreadcrumbComponent from "../components/CategoryBreadcrumbComponent";
import ProductListComponent from "../components/ProductListComponent";
import VariationComponent from "../components/VariationComponent";
import appService from "../../../services/appService";
import alertService from "../../../services/alertService";
import { useHead } from '@vueuse/head';
import activityEnum from "../../../enums/modules/activityEnum";
import 'vue-inner-image-zoom/lib/vue-inner-image-zoom.css';

import { useCanvas } from "../../../composables/canvas";
import InnerImageZoom from 'vue-inner-image-zoom';

export default {
    name: "ProductDetailsComponent",
    components: {
        VariationComponent,
        ProductListComponent,
        CategoryBreadcrumbComponent,
        starRating,
        Swiper,
        SwiperSlide,
        LoadingComponent,
        'inner-image-zoom': InnerImageZoom
    },
    setup() {
        const thumbsSwiper = ref(null);
        const setThumbsSwiper = (swiper) => {
            thumbsSwiper.value = swiper;
        };
        const { openCanvas } = useCanvas();
        return {
            thumbsSwiper,
            setThumbsSwiper,
            modules: [FreeMode, Navigation, Thumbs],
            openCanvas
        }
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            enums: {
                activityEnum: activityEnum,
            },
            props: {
                search: {
                    slug: null,
                    review_limit: 3
                }
            },
            enableAddToCardButton: true,
            isAddingToCart: false,
            isTogglingWishlist: false,
            selectedVariation: null,
            productArray: {},
            variationComponent: false,
            initProduct: {
                isVariation: false,
                variationId: null,
                sku: null,
                stock: 0,
                quantity: 1,
                discount: 0,
                price: 0,
                oldPrice: 0,
                totalPrice: 0,
                maximum_purchase_quantity: 0
            },
            temp: {
                name: "",
                image: "",
                isVariation: false,
                variationId: null,
                productId: 0,
                sku: null,
                stock: 0,
                taxes: {},
                shipping: {},
                quantity: 1,
                discount: 0,
                price: 0,
                oldPrice: 0,
                totalPrice: 0,
                maximum_purchase_quantity: 0,
                purchasable: 0,
                stock_visibility: 'no visible'
            },
        }
    },
    computed: {
        setting: function () {
            return this.$store.getters['frontendSetting/lists'];
        },
        categories: function () {
            return this.$store.getters["frontendProductCategory/ancestorsAndSelf"];
        },
        initialVariations: function () {
            return this.$store.getters["frontendProductVariation/initialVariation"];
        },
        product: function () {
            return this.$store.getters["frontendProduct/show"];
        },
        displayProduct: function () {
            return this.product || {};
        },
        images: function () {
            return this.$store.getters["frontendProduct/showImages"];
        },
        videos: function () {
            return this.$store.getters["frontendProduct/showVideos"];
        },
        reviews: function () {
            return this.$store.getters["frontendProduct/showReviews"];
        },
        relatedProducts: function () {
            return this.$store.getters["frontendProduct/relatedProducts"];
        },
    },
    mounted() {
        this.show();
        this.showRelatedProduct();
    },
    methods: {
        onlyNumber: function (e) {
            return appService.onlyNumber(e);
        },
        currencyFormat: function (amount, decimal, currency, position) {
            return appService.currencyFormat(amount, decimal, currency, position);
        },
        multiTargets: function (event, commonBtnClass, commonDivClass, targetID) {
            targetService.multiTargets(event, commonBtnClass, commonDivClass, targetID)
        },
        wishlist: function (toggle) {
            this.isTogglingWishlist = true;
            this.$store.dispatch("frontendWishlist/toggle", {
                product_id: this.displayProduct.id,
                toggle: toggle
            }).then((res) => {
                this.$store.commit('frontendProduct/show', { ...this.displayProduct, wishlist: toggle });
                this.isTogglingWishlist = false;
            }).catch((err) => {
                if (err.response && err.response.status === 401) {
                    this.$store.commit('frontendProduct/show', { ...this.displayProduct, wishlist: false });
                    router.push({ name: "auth.login" });
                }
                this.isTogglingWishlist = false;
            });
        },
        readMore: function () {
            this.props.search.review_limit += 1;
            this.show();
        },
        show: function () {
            if (typeof this.$route.params.slug !== "undefined") {
                this.loading.isActive = true;
                this.props.search.slug = this.$route.params.slug;
                this.$store.dispatch("frontendProduct/show", this.props.search).then((res) => {
                    this.initProduct = {
                        isVariation: false,
                        variationId: null,
                        sku: res.data.data.sku,
                        stock: res.data.data.stock,
                        quantity: 1,
                        discount: 0,
                        price: res.data.data.price,
                        oldPrice: res.data.data.old_price,
                        totalPrice: res.data.data.price,
                        maximum_purchase_quantity: res.data.data.maximum_purchase_quantity,
                        purchasable: res.data.data.can_purchasable
                    };
                    this.temp = {
                        name: res.data.data.name,
                        image: res.data.data.image,
                        isVariation: false,
                        variationId: null,
                        productId: res.data.data.id,
                        sku: res.data.data.sku,
                        stock: res.data.data.stock,
                        taxes: res.data.data.taxes,
                        shipping: res.data.data.shipping,
                        quantity: 1,
                        discount: 0,
                        price: res.data.data.price,
                        oldPrice: res.data.data.old_price,
                        totalPrice: res.data.data.price,
                        maximum_purchase_quantity: res.data.data.maximum_purchase_quantity,
                        purchasable: res.data.data.can_purchasable,
                        stock_visibility: 'no visible'
                    };

                    this.$store.dispatch("frontendProductCategory/ancestorsAndSelf", res.data.data.category_slug).then((categoryRes) => {
                        this.loading.isActive = false;
                    }).catch((err) => {
                        this.loading.isActive = false;
                    });

                    this.$store.dispatch("frontendProductVariation/initialVariation", res.data.data.id).then((initVariationRes) => {
                        if (initVariationRes.data.data.length > 0) {
                            this.variationComponent = true;
                        }
                        if (!initVariationRes.data.data.length && res.data.data.stock > 0) {
                            this.enableAddToCardButton = false;
                        }
                        this.loading.isActive = false;
                    }).catch((err) => {
                        this.loading.isActive = false;
                    });

                    if (Object.keys(res.data.data.seo) && res.data.data.seo.title && res.data.data.seo.description) {
                        let metaData = [
                            { name: 'title', content: res.data.data.seo.title },
                            { name: 'description', content: res.data.data.seo.description },
                        ];

                        if (res.data.data.seo.thumb && res.data.data.seo.cover) {
                            metaData.push({ content: res.data.data.seo.thumb });
                            metaData.push({ content: res.data.data.seo.cover });
                        }

                        useHead({
                            title: this.setting.company_name + ' - ' + res.data.data.seo.title,
                            meta: metaData
                        });
                    }
                }).catch((err) => {
                    this.loading.isActive = false;
                });
            }
        },
        showRelatedProduct: function () {
            if (typeof this.$route.params.slug !== "undefined") {
                this.loading.isActive = true;
                this.props.search.slug = this.$route.params.slug;
                this.$store.dispatch("frontendProduct/relatedProducts", {
                    slug: this.$route.params.slug,
                    rand: 8
                }).then((res) => {
                    this.loading.isActive = false;
                }).catch((err) => {
                    this.loading.isActive = false;
                });
            }
        },
        selectedVariationMethod: function (variation) {
            this.enableAddToCardButton = true;
            this.selectedVariation = null;

            this.temp.isVariation = this.initProduct.isVariation;
            this.temp.variationId = this.initProduct.variationId;
            this.temp.sku = this.initProduct.sku;
            this.temp.stock = this.initProduct.stock;
            this.temp.quantity = this.initProduct.quantity;
            this.temp.discount = this.initProduct.discount;
            this.temp.price = this.initProduct.price;
            this.temp.oldPrice = this.initProduct.oldPrice;
            this.temp.totalPrice = this.initProduct.price;
            this.temp.maximum_purchase_quantity = this.initProduct.maximum_purchase_quantity;
            this.temp.purchasable = this.initProduct.purchasable;

            if (variation) {
                this.selectedVariation = variation;

                this.temp.isVariation = true;
                this.temp.variationId = variation.id;
                this.temp.sku = variation.sku;
                this.temp.stock = variation.stock;
                this.temp.quantity = 1;
                this.temp.discount = 0;
                this.temp.price = variation.price;
                this.temp.oldPrice = variation.old_price;
                this.temp.totalPrice = variation.price;
                this.temp.maximum_purchase_quantity = variation.maximum_purchase_quantity;
                this.temp.purchasable = this.initProduct.purchasable;

                if (variation.stock > 0) {
                    this.enableAddToCardButton = false;
                }
            }
        },
        quantityUp: function () {
            if (this.temp.quantity === 0) {
                this.temp.quantity = 1;
            }
            if (this.temp.quantity > this.temp.stock) {
                this.temp.quantity = this.temp.stock
            }

            if (this.temp.quantity > this.temp.maximum_purchase_quantity) {
                alertService.error(this.$t('message.purchase_limit_exceeded'));
                this.temp.quantity = this.temp.maximum_purchase_quantity
            }
            this.totalPriceSetup();
        },
        quantityIncrement: function () {
            this.temp.quantity++;
            if (this.temp.quantity <= 0) {
                this.temp.quantity = 1;
            }

            if (this.temp.quantity > this.temp.stock) {
                this.temp.quantity--;
            }
            if (this.temp.quantity > this.temp.maximum_purchase_quantity) {
                this.temp.quantity--;
            }
            this.totalPriceSetup();
        },
        quantityDecrement: function () {
            this.temp.quantity--;
            if (this.temp.quantity <= 0) {
                this.temp.quantity = 1;
            }
            this.totalPriceSetup();
        },
        totalPriceSetup: function () {
            this.temp.totalPrice = (this.temp.price * this.temp.quantity);
        },
        addToCart: function () {
            this.enableAddToCardButton = true;
            this.isAddingToCart = true;
            this.productArray = {
                name: this.temp.name,
                product_id: this.temp.productId,
                image: this.temp.image,
                variation_names: '',
                variation_id: this.temp.variationId,
                sku: this.temp.sku,
                stock: this.temp.stock,
                taxes: this.temp.taxes,
                shipping: this.temp.shipping,
                quantity: this.temp.quantity,
                discount: this.temp.discount,
                price: this.temp.price,
                old_price: this.temp.oldPrice,
                total_price: this.temp.totalPrice,
                maximum_purchase_quantity: this.temp.maximum_purchase_quantity
            }

            const dispatchCart = (reservedUntil = null) => {
                if (reservedUntil) {
                    this.productArray.reserved_until = reservedUntil;
                }

                if (this.selectedVariation) {
                    this.$store.dispatch("frontendProductVariation/ancestorsToString", this.selectedVariation.id).then((res) => {
                        this.productArray.variation_names = res.data.data;
                        this.variationComponent = false;
                        this.$store.dispatch("frontendCart/lists", this.productArray).then((res) => {
                            if (reservedUntil) {
                                alertService.success(this.$t('message.low_stock_reserved_toast', { minutes: this.temp.reservedMinutes || 5 }));
                            } else {
                                alertService.success(this.$t('message.add_to_cart'));
                            }
                            this.openCanvas('cart-canvas');
                            this.variationComponent = true;
                            this.productArray = {};
                            this.selectedVariation = null;
                            this.temp.isVariation = this.initProduct.isVariation;
                            this.temp.variationId = this.initProduct.variationId;
                            this.temp.sku = this.initProduct.sku;
                            this.temp.stock = this.initProduct.stock;
                            this.temp.quantity = this.initProduct.quantity;
                            this.temp.discount = this.initProduct.discount;
                            this.temp.price = this.initProduct.price;
                            this.temp.oldPrice = this.initProduct.oldPrice;
                            this.temp.totalPrice = this.initProduct.price;
                            this.temp.maximum_purchase_quantity = this.initProduct.maximum_purchase_quantity;
                            this.isAddingToCart = false;
                        }).catch((err) => {
                            alertService.error(this.$t('message.maximum_quantity'));
                            this.variationComponent = true;
                            this.selectedVariation = null;
                            this.temp.stock = this.initProduct.stock;
                            this.temp.quantity = this.initProduct.quantity;
                            this.isAddingToCart = false;
                        });
                    }).catch((err) => {
                        this.isAddingToCart = false;
                    });
                } else {
                    this.$store.dispatch("frontendCart/lists", this.productArray).then((res) => {
                        if (reservedUntil) {
                            alertService.success(this.$t('message.low_stock_reserved_toast', { minutes: this.temp.reservedMinutes || 5 }));
                        } else {
                            alertService.success(this.$t('message.add_to_cart'));
                        }
                        this.openCanvas('cart-canvas');
                        this.enableAddToCardButton = false;
                        this.productArray = {};
                        this.selectedVariation = null;
                        this.temp.isVariation = this.initProduct.isVariation;
                        this.temp.variationId = this.initProduct.variationId;
                        this.temp.sku = this.initProduct.sku;
                        this.temp.stock = this.initProduct.stock;
                        this.temp.quantity = this.initProduct.quantity;
                        this.temp.discount = this.initProduct.discount;
                        this.temp.price = this.initProduct.price;
                        this.temp.oldPrice = this.initProduct.oldPrice;
                        this.temp.totalPrice = this.initProduct.price;
                        this.temp.maximum_purchase_quantity = this.initProduct.maximum_purchase_quantity;
                        this.isAddingToCart = false;
                    }).catch((err) => {
                        alertService.error(this.$t('message.maximum_quantity'));
                        this.enableAddToCardButton = false;
                        this.selectedVariation = null;
                        this.temp.stock = this.initProduct.stock;
                        this.temp.quantity = this.initProduct.quantity;
                        this.isAddingToCart = false;
                    });
                }
            };

            // If stock === 1, attempt reservation on backend first
            if (parseInt(this.temp.stock) === 1) {
                axios.post('/frontend/cart/reserve', {
                    product_id: this.temp.productId,
                    variation_id: this.temp.variationId,
                    cart_token: this.$store.getters['frontendCart/cartToken'],
                    quantity: this.temp.quantity
                }).then(res => {
                    this.temp.reservedMinutes = res.data.data.minutes;
                    dispatchCart(res.data.data.expires_at);
                }).catch(err => {
                    const errorMsg = err.response && err.response.data && err.response.data.message 
                        ? err.response.data.message 
                        : this.$t('message.product_already_reserved');
                    alertService.error(errorMsg);
                    this.enableAddToCardButton = false;
                    this.isAddingToCart = false;
                });
            } else {
                dispatchCart(null);
            }
        }
    },
    watch: {
        $route() {
            this.show();
            this.showRelatedProduct();
        }
    }
}
</script>